import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { getByPath, interpolate } from '../i18n/helpers';
import { legacyFlat } from '../i18n/locales/legacyFlat';
import frMessages from '../i18n/locales/fr';
import arMessages from '../i18n/locales/ar';
import { useAuth } from './AuthContext';
import { saveUiPreferences } from '../services/settingsAPI';
import { subscribeUiPreferences } from '../services/uiPreferencesBridge';

const nestedLocales = {
  fr: frMessages,
  ar: arMessages,
};

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const { isAuthenticated, AUTH_DEV_BYPASS } = useAuth();
  const [language, setLanguage] = useState('fr');
  const skipSaveRef = useRef(true);

  useEffect(() => {
    return subscribeUiPreferences((prefs) => {
      if (prefs.language && nestedLocales[prefs.language]) {
        skipSaveRef.current = true;
        setLanguage(prefs.language);
      }
    });
  }, []);

  useEffect(() => {
    document.documentElement.lang = language === 'ar' ? 'ar' : 'fr';
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
  }, [language]);

  useEffect(() => {
    if (skipSaveRef.current) {
      skipSaveRef.current = false;
      return;
    }
    if (!isAuthenticated || AUTH_DEV_BYPASS) return;
    saveUiPreferences({ language }).catch((err) => {
      console.warn('Failed to save language preference to D1:', err);
    });
  }, [language, isAuthenticated, AUTH_DEV_BYPASS]);

  const lookup = useCallback(
    (key) => {
      const safeKey = key == null ? '' : String(key);
      let raw;

      if (safeKey.includes('.')) {
        raw = getByPath(nestedLocales[language], safeKey);
        if (typeof raw !== 'string') {
          raw = getByPath(nestedLocales.fr, safeKey);
        }
      } else {
        raw = legacyFlat[language]?.[safeKey];
        if (typeof raw !== 'string') {
          raw = legacyFlat.fr?.[safeKey];
        }
        if (typeof raw !== 'string') {
          raw = getByPath(nestedLocales[language], safeKey);
        }
        if (typeof raw !== 'string') {
          raw = getByPath(nestedLocales.fr, safeKey);
        }
      }

      return typeof raw === 'string' ? raw : undefined;
    },
    [language]
  );

  const t = useCallback(
    (key, vars) => {
      const resolved = lookup(key);
      const str = resolved !== undefined ? resolved : key;
      return interpolate(str, vars);
    },
    [lookup]
  );

  const changeLanguage = useCallback((newLanguage) => {
    if (nestedLocales[newLanguage] && legacyFlat[newLanguage]) {
      setLanguage(newLanguage);
    }
  }, []);

  const pick = useCallback(
    (path) =>
      getByPath(nestedLocales[language], path) ??
      getByPath(nestedLocales.fr, path),
    [language]
  );

  const value = {
    language,
    setLanguage,
    changeLanguage,
    t,
    pick,
    isRTL: language === 'ar',
  };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};
